import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_URL as API } from "../config/backend";
import { getAcquisition, clearAcquisition } from "../utils/acquisition";
import { trackEvent } from "../utils/analytics";

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth via httpOnly cookies (envoyées automatiquement avec withCredentials: true)

// CSRF double-submit — attache ``X-CSRF-Token`` (= cookie ``csrf_token``) à toute
// requête mutante de l'espace association. SANS ça, le middleware CSRF (deny-by-default)
// rejetait en 403 le POST /associations/auth/logout (cookie présent, header absent) →
// la déconnexion n'atteignait jamais le handler, le cookie n'était jamais effacé et la
// session « ressuscitait » (bug signalé : impossible de se déconnecter). Même logique
// que le client principal (AuthContext). Lecture inline du cookie (pas httpOnly).
function _readCsrfCookie() {
  try {
    const m = (typeof document !== "undefined" ? document.cookie : "").match(
      /(?:^|;\s*)csrf_token=([^;]+)/
    );
    return m ? m[1] : null;
  } catch (_) {
    return null;
  }
}
api.interceptors.request.use(
  (config) => {
    const method = (config.method || "get").toLowerCase();
    if (["post", "put", "patch", "delete"].includes(method)) {
      const token = _readCsrfCookie();
      if (token) {
        config.headers = config.headers || {};
        if (!config.headers["X-CSRF-Token"]) {
          config.headers["X-CSRF-Token"] = token;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Association Auth Context
const AssociationAuthContext = createContext(null);

export const useAssociationAuth = () => useContext(AssociationAuthContext);

export const AssociationAuthProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  // Track whether the initial /associations/auth/me returned 401. If it did,
  // this tab is NOT an association session — don't keep hammering the endpoint
  // on every visibility change. We only re-check on demand (login, manual call).
  const [hasAssoSession, setHasAssoSession] = useState(false);
  // ── STATUT JURIDIQUE DE L'ASSOCIATION (LOT 1b, 05/09/2026) ──────────────
  // `GET /associations/auth/legal-status` (`routes/associations.py:2858`)
  // existait depuis longtemps et n'avait AUCUN appelant : une association ne
  // pouvait donc jamais apprendre que ses documents avaient changé. Depuis
  // #2355 la garde la REFUSE en 451 sur la publication et le checkout — sans
  // ce statut, elle se serait heurtée au mur sans jamais avoir été prévenue.
  //
  // ATTENTION AU FAUX AMI : `account.legal_status` (`schemas.py:520`) désigne
  // le statut SIRET (`with_siret` / `pending` / `no_siret`), PAS l'acceptation
  // des documents. D'où un état SÉPARÉ, et surtout pas une lecture de `account`.
  const [legalStatus, setLegalStatus] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/associations/auth/me");
      setAccount(response.data);
      setHasAssoSession(true);
      // On enchaîne SANS bloquer la session : une asso authentifiée le reste
      // même si cet appel échoue. Le mur 451 reste le filet de sécurité.
      api.get("/associations/auth/legal-status")
        .then(r => setLegalStatus(r.data))
        .catch(() => { /* pas de statut : la garde 451 prendra le relais */ });
      try { sessionStorage.setItem("asso_logged_in", "true"); } catch {}
    } catch (error) {
      setAccount(null);
      setHasAssoSession(false);
      try { sessionStorage.removeItem("asso_logged_in"); } catch {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Re-check auth when tab becomes visible — ONLY if the user has (or had) an
  // association session. This prevents regular users from generating repeated
  // 401s on /associations/auth/me each time they switch tabs.
  useEffect(() => {
    if (!hasAssoSession) return;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [checkAuth, hasAssoSession]);

  // Polling checkAuth every 5 min (only when tab is visible AND we are actually
  // logged in as an association) to detect cookie expiry.
  useEffect(() => {
    if (!account) return;
    const POLL_INTERVAL = 5 * 60 * 1000;
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") {
        api.get("/associations/auth/me").catch(() => {
          setAccount(null);
          setHasAssoSession(false);
          localStorage.removeItem("asso_token");
          sessionStorage.removeItem("asso_logged_in");
          toast.error("Votre session a expiré. Veuillez vous reconnecter.");
        });
      }
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [account]);

  const login = async (email, password, code) => {
    const response = await api.post("/associations/auth/login", { email, password, code });
    // 2FA email-OTP (parité user) : le 1er appel (sans code) renvoie
    // {requires_2fa:true} sans `account` → on remonte le signal au formulaire,
    // qui affiche le champ code et rappelle login(email, password, code).
    if (response.data?.requires_2fa) {
      return { requires_2fa: true, email: response.data.email };
    }
    const { account } = response.data;
    // Le cookie httpOnly est set par le backend
    setAccount(account);
    return account;
  };

  // Inscription account-first (Lot 1) : crée le compte + connecte direct. Le
  // backend pose le cookie httpOnly ; on hydrate la session comme au login.
  const signup = async (official_name, email, password, accept_cgu = true, referral_code = undefined) => {
    // Provenance première (utm, site d'origine), comme l'inscription utilisateur.
    const acquisition = getAcquisition();
    const response = await api.post("/associations/auth/signup", {
      official_name, email, password, accept_cgu,
      ...(referral_code ? { referral_code } : {}),
      ...(acquisition ? { acquisition } : {}),
    });
    clearAcquisition();
    trackEvent("sign_up", { method: "association" });
    const { account } = response.data;
    setAccount(account);
    setHasAssoSession(true);
    try { sessionStorage.setItem("asso_logged_in", "true"); } catch {}
    return account;
  };

  const logout = async () => {
    // P0 PII (audit Phoenix 23/08, ordre durci revue CodeRabbit #2186) — purge
    // du brouillon de publication EN PREMIER, avant l'await réseau (axios sans
    // timeout : une requête qui pend ne doit jamais retenir les données du
    // compte précédent). Les deux clés : un compte asso et un compte user
    // peuvent se succéder sur le même appareil.
    // R2/R3 (#2186) : purge via la SOURCE UNIQUE bornée (utils/draftPurge) —
    // jamais bloquant — PUIS signal cross-tab : sessionStorage étant propre à
    // chaque onglet, le brouillon asso (adresse d'expédition…) survivait dans
    // les AUTRES onglets d'un poste partagé. Signal DISTINCT de gively_logout
    // (qui déconnecterait aussi la session utilisateur des autres onglets).
    try {
      const { purgePublishDrafts, broadcastAssoLogout } = await import("../utils/draftPurge");
      await purgePublishDrafts();
      const { safeLocalStorage } = await import("../utils/safeStorage");
      broadcastAssoLogout(safeLocalStorage);
    } catch {}
    // Session Stripe des composants Versements (09/10/2026), comme côté
    // utilisateur : « Only call logout when your user logs out of your app »
    // (doc Stripe). Sans attendre : jamais bloquant pour la déconnexion.
    import("../components/versements/connectStripe")
      .then((m) => m.deconnecterStripeConnect())
      .catch(() => {});
    try {
      await api.post("/associations/auth/logout");
    } catch (error) {
      // Logout error ignored
    }
    // Clear all session traces
    setAccount(null);
    // GO TOTAL 09/08/2026 (P2-5) — purge du cache messagerie de l'asso.
    try {
      const _aid = account?.association_id;
      if (_aid) {
        localStorage.removeItem(`conversations_cache_${_aid}`);
        localStorage.removeItem(`conversations_cache_${_aid}_t`);
      }
      localStorage.removeItem("conversations_cache");
      localStorage.removeItem("conversations_cache_t");
    } catch {}
    localStorage.removeItem("asso_token");
    sessionStorage.removeItem("asso_logged_in");
    // Reload complet vers la page de connexion asso : état mémoire vierge,
    // plus aucune session résiduelle (cohérent avec le logout user).
    window.location.replace("/association/login");
  };

  /** Relit le statut juridique — appelé après une acceptation réussie. */
  const refreshLegalStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/associations/auth/legal-status");
      setLegalStatus(data);
    } catch {
      // Échec de relecture : on remet à null plutôt que de garder un statut
      // périmé qui rouvrirait la modale en boucle sur un document déjà accepté.
      setLegalStatus(null);
    }
  }, []);

  return (
    <AssociationAuthContext.Provider value={{ account, loading, login, signup, logout, checkAuth, legalStatus, refreshLegalStatus }}>
      {children}
    </AssociationAuthContext.Provider>
  );
};

// Icons
const BuildingIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const HeartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const EuroIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const UsersIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
  </svg>
);

const UploadIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
  </svg>
);

const DocumentIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const DownloadIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const ChartIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const ArrowLeftIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);

const MapPinIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const UserIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MessageIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

// Logo Component — uses the official brand asset, never recreated inline.
const Logo = () => (
  <Link to="/" className="flex items-center gap-3">
    <img
      src="/logo-gively.svg"
      alt="Gively"
      width={40}
      height={40}
      style={{ display: 'block', flexShrink: 0 }}
    />
    <span className="font-extrabold text-xl text-[#1DA1F2]" style={{ fontFamily: 'Manrope, sans-serif' }}>
      Gively
    </span>
  </Link>
);

// ==================== PAGES ====================

// Landing Page: Devenir Association Partenaire

